// ─────────────────────────────────────────────────────────────────────────────
// radarAgents — ProximityRadar data model (Chunk 5, desktop).
//
// The radar is a SECOND CONSUMER of the ACTIVITY index (GET /api/rexy/activity),
// not a link in her chain. It reads who/when off the board and plots by recency;
// it writes nothing and invents nothing. Bands match ACTIVITY's own rolling 12-hour
// window: inner < 2h, middle 2–6h, outer 6–12h.
//
// IDENTITY IS INHERITED, NEVER TRANSLATED: the board builds `who` as
// details.agent || details.domain, so agent rows arrive as ARTHUR/MACK/TRUFFLE and
// domain-board / SECURITY rows arrive as CODE / SECURITY. The radar plots `who`
// verbatim — no mapping table, no roster lookup. Mixed agent/domain dots are the
// intended shape (inventing agent identities to "normalise" them is the abolished
// synthetic-dashboard failure).
// ─────────────────────────────────────────────────────────────────────────────

export interface RadarAgent {
  id: string;
  name: string;
  domain: string;
  lastActive: number; // Unix ms
  colour: string;
  angle: number; // radians — stable hash-derived bearing
}

// Locked domain → dot colour.
export const DOMAIN_COLOURS: Record<string, string> = {
  PERSONAL: '#FFFFFF',
  TRADING: '#F59E0B',
  CODE: '#0070F3',
  SECURITY: '#EF4444',
  GROWTH: '#22C55E',
  RESEARCH: '#FFFFFF',
  BUSINESS: '#FFFFFF',
  // FRONTIER was missing and fell through to the #FFFFFF default, rendering
  // indistinguishable from PERSONAL / RESEARCH / BUSINESS. Owner ruling
  // 2026-08-03: #a78bfa — the one colour from the phone surface's original
  // palette that collides with nothing here and reads distinctly against all
  // eight. Added HERE rather than on the phone alone so both surfaces carry it.
  FRONTIER: '#a78bfa',
};

export function colourForDomain(domain: string): string {
  return DOMAIN_COLOURS[domain.toUpperCase()] ?? '#FFFFFF';
}

// Ring by recency (locked to ACTIVITY's 12-hour window). 0 = not shown (>12h).
// The 12h boundary is inclusive — ACTIVITY's own cutoff is inclusive, so a row at
// exactly the edge still belongs to the outer band rather than vanishing.
export const RADAR_WINDOW_MS = 12 * 60 * 60 * 1000;
export function ringForAge(ageMs: number): number {
  if (ageMs < 0) return 1; // clock skew guard — treat as newest
  if (ageMs < 2 * 60 * 60 * 1000) return 1; // inner  — < 2h
  if (ageMs < 6 * 60 * 60 * 1000) return 2; // middle — 2–6h
  if (ageMs <= RADAR_WINDOW_MS) return 3; // outer  — 6–12h (inclusive edge)
  return 0; // older than the window — not shown
}
